"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { format, startOfMonth, endOfMonth, getDate, addMonths, subMonths } from "date-fns"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CalendarIcon, ChevronLeft, ChevronRight, Loader2, X, Plus, Sparkles, Sun, Moon, Calendar } from "lucide-react"
import { toast } from "react-hot-toast"
import CustomCalendar from "./CustomCalendar"
import { API_ENDPOINTS } from "../../constants/config"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

const HolidayScheduler = () => {
  const [holidays, setHolidays] = useState([])
  const [holidayName, setHolidayName] = useState("")
  const [selectedDate, setSelectedDate] = useState("")
  const [dateRange, setDateRange] = useState({ from: "", to: "" })
  const [type, setType] = useState("school")
  const [weekendConfig, setWeekendConfig] = useState({
    saturday: "none",
    sunday: "all",
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [calendarMonth, setCalendarMonth] = useState(new Date())
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null)
  const [holidayLoading, setHolidayLoading] = useState(false)
  const [weekendLoading, setWeekendLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(null)
  const [user, setUser] = useState(null)
  const [holidayToDelete, setHolidayToDelete] = useState(null)
  const [showPastHolidayError, setShowPastHolidayError] = useState(false)
  const [academicYear, setAcademicYear] = useState("")
  const [availableYears, setAvailableYears] = useState([])
  const [applyTo, setApplyTo] = useState("all")
  const [selectedClassIds, setSelectedClassIds] = useState([])
  const [classes, setClasses] = useState([])
  const [classesLoading, setClassesLoading] = useState(false)
  const [creationMode, setCreationMode] = useState("single")
  const [selectedDays, setSelectedDays] = useState([])

  // Calculate academic year and available years (April to March)
  useEffect(() => {
    const currentDate = new Date()
    const currentMonth = currentDate.getMonth() // 0-11
    const currentYear = currentDate.getFullYear()
    
    // If month is Jan-Mar (0-2), academic year started previous year
    // If month is Apr-Dec (3-11), academic year started this year
    const academicStartYear = currentMonth < 3 ? currentYear - 1 : currentYear
    const academicEndYear = academicStartYear + 1
    
    setAcademicYear(`${academicStartYear}-${academicEndYear}`)
    
    // Generate available years (current year + 2 years before and after)
    const years = []
    for (let i = -2; i <= 2; i++) {
      const startYear = academicStartYear + i
      const endYear = startYear + 1
      years.push(`${startYear}-${endYear}`)
    }
    setAvailableYears(years)
  }, [])

  // Update calendar month when academic year changes
  useEffect(() => {
    if (academicYear) {
      const [startYear] = academicYear.split('-').map(Number)
      // Set calendar to April of the selected academic year
      setCalendarMonth(new Date(startYear, 3, 1)) // April 1st
    }
  }, [academicYear])

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"))
    if (!userData || !userData.school) {
      toast.error("School information not found. Please log in again.")
      return
    }
    setUser(userData)
    fetchHolidays()
  }, [])

  useEffect(() => {
    if (!user?.school) return
    setClassesLoading(true)
    fetch(API_ENDPOINTS.classesSchool(user.school), {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error("Failed to fetch classes")))
      .then((data) => setClasses(data?.data?.classes || []))
      .catch(() => setClasses([]))
      .finally(() => setClassesLoading(false))
  }, [user?.school])

  const fetchHolidays = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(API_ENDPOINTS.holidays, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to fetch holidays")
      }

      const data = await response.json()
      setHolidays(data.holidays || [])
      setWeekendConfig(data.weekendConfig || { saturday: "none", sunday: "all" })
      setError("")
    } catch (error) {
      setError(error.message || "Failed to fetch holidays")
      toast.error("Failed to fetch holidays")
      console.error("Fetch error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    )
  }

  const handleAddHoliday = async () => {
    if (creationMode === "single") {
      if (
        !holidayName ||
        (type !== "vacation" && !selectedDate) ||
        (type === "vacation" && (!dateRange.from || !dateRange.to))
      ) {
        setError("Please fill in all required holiday fields")
        toast.error("Please fill in all required holiday fields")
        return
      }
      if (applyTo === "selected" && selectedClassIds.length === 0) {
        setError("Please select at least one class when applying to selected classes")
        toast.error("Select at least one class")
        return
      }
    } else {
      if (!holidayName || !dateRange.from || !dateRange.to || selectedDays.length === 0) {
        setError("Please fill in all required fields including days of week")
        toast.error("Please fill in all required fields")
        return
      }
    }
    if (!user || !user.school) {
      setError("School information not found. Please log in again.")
      toast.error("School information not found. Please log in again.")
      return
    }

    setHolidayLoading(true)
    try {
      let endpoint
      let requestBody
      if (creationMode === "bulk") {
        endpoint = API_ENDPOINTS.holidaysBulkAdd
        requestBody = {
          name: holidayName,
          dateRange: dateRange,
          daysOfWeek: selectedDays,
          schoolId: user.school,
          applicableClasses: applyTo === "selected" ? selectedClassIds : undefined,
        }
      } else {
        endpoint = API_ENDPOINTS.holidaysAdd
        requestBody = {
          name: holidayName,
          type: type,
          schoolId: user.school,
          applicableClasses: applyTo === "selected" ? selectedClassIds : undefined,
        }
        if (type === "vacation") {
          requestBody.dateRange = dateRange
        } else {
          requestBody.date = selectedDate
        }
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to add holiday")
      }

      const data = await response.json()
      setError("")
      toast.success(data.message || "Holiday added successfully!")
      fetchHolidays()
      setHolidayName("")
      setSelectedDate("")
      setDateRange({ from: "", to: "" })
      setSelectedClassIds([])
      setSelectedDays([])
    } catch (error) {
      setError(error.message || "Failed to add holiday")
      toast.error(error.message || "Failed to add holiday")
      console.error("Add holiday error:", error)
    } finally {
      setHolidayLoading(false)
    }
  }

  const handleDeleteHoliday = async (holiday) => {
    const currentDate = new Date()
    currentDate.setHours(0, 0, 0, 0) // Set to start of day for proper comparison

    // Check if holiday is in the past
    let holidayDate
    if (holiday.type === "vacation") {
      // For vacation periods, use the end date for comparison
      holidayDate = new Date(holiday.dateRange.to)
    } else {
      holidayDate = new Date(holiday.date)
    }
    holidayDate.setHours(0, 0, 0, 0)

    if (holidayDate < currentDate) {
      setShowPastHolidayError(true)
      return
    }

    setHolidayToDelete(holiday)
  }

  const confirmDelete = async () => {
    if (!holidayToDelete) return

    setDeleteLoading(holidayToDelete._id)
    try {
      const response = await fetch(API_ENDPOINTS.holiday(holidayToDelete._id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to delete holiday")
      }

      toast.success("Holiday deleted successfully!")
      fetchHolidays()
    } catch (error) {
      toast.error(error.message || "Failed to delete holiday")
      console.error("Delete holiday error:", error)
    } finally {
      setDeleteLoading(null)
      setHolidayToDelete(null)
    }
  }

  const handleUpdateWeekendConfig = async () => {
    setWeekendLoading(true)
    try {
      const requestBody = {
        weekendConfig: weekendConfig,
      }

      const response = await fetch(API_ENDPOINTS.holidaysUpdateConfig, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to update weekend configuration")
      }

      const data = await response.json()
      console.log("Success:", data)
      setError("")
      toast.success("Weekend settings updated successfully!")
      fetchHolidays()
    } catch (error) {
      setError(error.message || "Failed to update weekend configuration")
      toast.error(error.message || "Failed to update weekend configuration")
      console.error("Update weekend configuration error:", error)
    } finally {
      setWeekendLoading(false)
    }
  }

  const handleNextMonth = () => {
    setCalendarMonth(addMonths(calendarMonth, 1))
  }

  const handlePreviousMonth = () => {
    setCalendarMonth(subMonths(calendarMonth, 1))
  }

  // Filter holidays by academic year
  const getFilteredHolidays = () => {
    if (!academicYear) return holidays

    const [startYear, endYear] = academicYear.split('-').map(Number)
    const academicYearStart = new Date(startYear, 3, 1) // April 1st
    const academicYearEnd = new Date(endYear, 2, 31) // March 31st

    return holidays.filter((holiday) => {
      if (holiday.type === "vacation") {
        const holidayStart = new Date(holiday.dateRange.from)
        const holidayEnd = new Date(holiday.dateRange.to)
        return holidayStart <= academicYearEnd && holidayEnd >= academicYearStart
      } else {
        const holidayDate = new Date(holiday.date)
        return holidayDate >= academicYearStart && holidayDate <= academicYearEnd
      }
    })
  }

  const getHolidaysForCurrentMonth = () => {
    const startDate = startOfMonth(calendarMonth)
    const endDate = endOfMonth(calendarMonth)
    const filteredHolidays = getFilteredHolidays()

    return filteredHolidays.filter((holiday) => {
      if (holiday.type === "vacation") {
        const holidayStart = new Date(holiday.dateRange.from)
        const holidayEnd = new Date(holiday.dateRange.to)
        return holidayStart <= endDate && holidayEnd >= startDate
      } else {
        const holidayDate = new Date(holiday.date)
        return holidayDate >= startDate && holidayDate <= endDate
      }
    })
  }

  const getHolidayTypeColor = (type) => {
    const colors = {
      school: "bg-blue-100 text-blue-800",
      public: "bg-green-100 text-green-800",
      vacation: "bg-purple-100 text-purple-800",
      weekend: "bg-red-100 text-red-800",
    }
    return colors[type] || "bg-gray-100 text-gray-800"
  }

  // Helper function to check if a date has a holiday
  const getHolidayForDate = (date) => {
    // Format the date to compare
    const formattedDate = format(date, "yyyy-MM-dd")
    const filteredHolidays = getFilteredHolidays()

    // Check for single day holidays
    const singleDayHoliday = filteredHolidays.find(
      (holiday) => holiday.type !== "vacation" && format(new Date(holiday.date), "yyyy-MM-dd") === formattedDate,
    )

    if (singleDayHoliday) {
      return { ...singleDayHoliday, isWeekend: false }
    }

    // Check for vacation periods
    const vacationPeriod = filteredHolidays.find(
      (holiday) =>
        holiday.type === "vacation" &&
        new Date(formattedDate) >= new Date(holiday.dateRange.from) &&
        new Date(formattedDate) <= new Date(holiday.dateRange.to),
    )

    if (vacationPeriod) {
      return { ...vacationPeriod, isWeekend: false }
    }

    // Check for weekends based on configuration
    const dayOfWeek = date.getDay()
    const dayOfMonth = getDate(date)
    const isEvenDay = dayOfMonth % 2 === 0

    // Saturday is day 6
    if (dayOfWeek === 6) {
      if (
        weekendConfig.saturday === "all" ||
        (weekendConfig.saturday === "even" && isEvenDay) ||
        (weekendConfig.saturday === "odd" && !isEvenDay)
      ) {
        return { type: "weekend", name: "Saturday", isWeekend: true }
      }
    }

    // Sunday is day 0
    if (dayOfWeek === 0) {
      if (
        weekendConfig.sunday === "all" ||
        (weekendConfig.sunday === "even" && isEvenDay) ||
        (weekendConfig.sunday === "odd" && !isEvenDay)
      ) {
        return { type: "weekend", name: "Sunday", isWeekend: true }
      }
    }

    return null
  }

  const handleCalendarDayClick = (day) => {
    setSelectedCalendarDay(day)
    const formattedDate = format(day, "yyyy-MM-dd")
    setSelectedDate(formattedDate)
  }

  return (
    <div className="mx-auto py-6 px-4 max-w-[1800px]">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Holiday Scheduler</h1>
            <p className="text-gray-600 text-sm mt-1">Manage school holidays and weekend configurations</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Academic Year:</label>
              <Select value={academicYear} onValueChange={setAcademicYear}>
                <SelectTrigger className="w-[160px] border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View - Larger */}
        <Card className="lg:col-span-2 bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3 pt-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-gray-700" />
              <CardTitle className="text-gray-900 text-lg font-semibold">Holiday Calendar</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-8 relative">
            {isLoading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
            )}
            
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="outline"
                size="sm"
                className="hover:bg-gray-100"
                onClick={handlePreviousMonth}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-lg font-semibold text-gray-900">
                {format(calendarMonth, "MMMM yyyy")}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="hover:bg-gray-100"
                onClick={handleNextMonth}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <CustomCalendar
              currentMonth={calendarMonth}
              selectedDate={selectedCalendarDay}
              onDateClick={handleCalendarDayClick}
              getHolidayForDate={getHolidayForDate}
              weekendConfig={weekendConfig}
            />

            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-4">Legend</h4>
              <div className="flex flex-wrap gap-6">
                {[
                  { color: "border-red-500 bg-red-50", label: "Weekend" },
                  { color: "border-blue-500 bg-blue-50", label: "School" },
                  { color: "border-green-500 bg-green-50", label: "Public" },
                  { color: "border-purple-500 bg-purple-50", label: "Vacation" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded-full border-2 ${item.color}`}></div>
                    <span className="text-gray-700 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Holidays List */}
        <Card className="lg:col-span-1 bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3 pt-4 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon className="w-5 h-5 text-gray-700" />
              <CardTitle className="text-gray-900 text-lg font-semibold">Holidays</CardTitle>
            </div>
            
            {/* Holiday Type Breakdown */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between bg-blue-50 px-2 py-1.5 rounded">
                <span className="text-blue-700 font-medium">School</span>
                <span className="text-blue-900 font-bold">
                  {getFilteredHolidays().filter(h => h.type === 'school').length}
                </span>
              </div>
              <div className="flex items-center justify-between bg-green-50 px-2 py-1.5 rounded">
                <span className="text-green-700 font-medium">Public</span>
                <span className="text-green-900 font-bold">
                  {getFilteredHolidays().filter(h => h.type === 'public').length}
                </span>
              </div>
              <div className="flex items-center justify-between bg-purple-50 px-2 py-1.5 rounded">
                <span className="text-purple-700 font-medium">Vacation</span>
                <span className="text-purple-900 font-bold">
                  {getFilteredHolidays().filter(h => h.type === 'vacation').length}
                </span>
              </div>
              <div className="flex items-center justify-between bg-gray-100 px-2 py-1.5 rounded">
                <span className="text-gray-700 font-medium">Total</span>
                <span className="text-gray-900 font-bold">
                  {getFilteredHolidays().length}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-4 relative">
            {isLoading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                <Loader2 className="h-8 w-8 text-gray-600 animate-spin" />
              </div>
            )}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {getFilteredHolidays().length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No holidays for {academicYear}</p>
                </div>
              ) : (
                getFilteredHolidays().map((holiday, index) => (
                  <div
                    key={holiday._id || index}
                    className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{holiday.name}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs ${getHolidayTypeColor(holiday.type)}`}>
                            {holiday.type}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          {holiday.type === "vacation" ? (
                            <>
                              {format(new Date(holiday.dateRange.from), "MMM dd")} - {format(new Date(holiday.dateRange.to), "MMM dd, yyyy")}
                            </>
                          ) : (
                            format(new Date(holiday.date), "MMM dd, yyyy")
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {!holiday.applicableClasses || holiday.applicableClasses.length === 0
                            ? "Applicable to: All classes"
                            : `Classes: ${holiday.applicableClasses.map((c) => c.name || c.className).join(", ")}`}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteHoliday(holiday)}
                        disabled={deleteLoading === holiday._id}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Delete holiday"
                      >
                        {deleteLoading === holiday._id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuration Form */}
        <Card className="lg:col-span-3 bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3 pt-4 border-b border-gray-100">
            <CardTitle className="text-gray-900 text-lg font-semibold">Configure Holidays</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Holiday Creation */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Add Holiday</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCreationMode("single")}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        creationMode === "single"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Single
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreationMode("bulk")}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        creationMode === "bulk"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Bulk
                    </button>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Holiday Name</label>
                    <Input
                      placeholder={creationMode === "bulk" ? "e.g., Saturday Holidays" : "e.g., Diwali, Summer Break"}
                      value={holidayName}
                      onChange={(e) => setHolidayName(e.target.value)}
                      className="border-gray-300"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Apply to</label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="applyTo"
                          checked={applyTo === "all"}
                          onChange={() => setApplyTo("all")}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">All classes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="applyTo"
                          checked={applyTo === "selected"}
                          onChange={() => setApplyTo("selected")}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">Selected classes</span>
                      </label>
                    </div>
                    {applyTo === "selected" && (
                      <div className="border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto bg-gray-50">
                        {classesLoading ? (
                          <span className="text-sm text-gray-500">Loading classes...</span>
                        ) : classes.length === 0 ? (
                          <span className="text-sm text-gray-500">No classes found</span>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {classes.map((cls) => (
                              <label key={cls._id} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedClassIds.includes(cls._id)}
                                  onChange={(e) =>
                                    setSelectedClassIds((prev) =>
                                      e.target.checked ? [...prev, cls._id] : prev.filter((id) => id !== cls._id)
                                    )
                                  }
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm text-gray-700">{cls.name || cls.className}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {applyTo === "selected" && selectedClassIds.length > 0 && (
                          <p className="text-xs text-gray-500 mt-2">{selectedClassIds.length} class(es) selected</p>
                        )}
                      </div>
                    )}
                  </div>

                  {creationMode === "single" && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">Holiday Type</label>
                        <Select value={type} onValueChange={setType}>
                          <SelectTrigger className="border-gray-300">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="school">School Holiday</SelectItem>
                            <SelectItem value="public">Public Holiday</SelectItem>
                            <SelectItem value="vacation">Vacation Period</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                          {type === "vacation" ? "Vacation Period" : "Holiday Date"}
                        </label>
                        {type === "vacation" ? (
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              type="date"
                              placeholder="From"
                              value={dateRange.from}
                              onChange={(e) =>
                                setDateRange((prev) => ({
                                  ...prev,
                                  from: e.target.value,
                                }))
                              }
                              className="border-gray-300"
                            />
                            <Input
                              type="date"
                              placeholder="To"
                              value={dateRange.to}
                              onChange={(e) =>
                                setDateRange((prev) => ({
                                  ...prev,
                                  to: e.target.value,
                                }))
                              }
                              className="border-gray-300"
                            />
                          </div>
                        ) : (
                          <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="border-gray-300"
                          />
                        )}
                      </div>
                    </>
                  )}

                  {creationMode === "bulk" && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Date Range</label>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="date"
                          placeholder="From"
                          value={dateRange.from}
                          onChange={(e) =>
                            setDateRange((prev) => ({
                              ...prev,
                              from: e.target.value,
                            }))
                          }
                          className="border-gray-300"
                        />
                        <Input
                          type="date"
                          placeholder="To"
                          value={dateRange.to}
                          onChange={(e) =>
                            setDateRange((prev) => ({
                              ...prev,
                              to: e.target.value,
                            }))
                          }
                          className="border-gray-300"
                        />
                      </div>
                    </div>
                  )}

                  {creationMode === "bulk" && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Select Days of Week</label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { label: "Sun", value: 0 },
                          { label: "Mon", value: 1 },
                          { label: "Tue", value: 2 },
                          { label: "Wed", value: 3 },
                          { label: "Thu", value: 4 },
                          { label: "Fri", value: 5 },
                          { label: "Sat", value: 6 },
                        ].map((day) => (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => toggleDay(day.value)}
                            className={`p-2 rounded border text-sm font-medium transition-colors ${
                              selectedDays.includes(day.value)
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Selected: {selectedDays.length} {selectedDays.length === 1 ? "day" : "days"}
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={handleAddHoliday}
                    disabled={holidayLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {holidayLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {creationMode === "bulk" ? "Creating Holidays..." : "Adding..."}
                      </>
                    ) : (
                      creationMode === "bulk" ? "Create Bulk Holidays" : "Add Holiday"
                    )}
                  </Button>
                </div>
              </div>

              {/* Weekend Configuration */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Weekend Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Saturday Schedule</label>
                    <Select
                      value={weekendConfig.saturday}
                      onValueChange={(value) =>
                        setWeekendConfig((prev) => ({
                          ...prev,
                          saturday: value,
                        }))
                      }
                    >
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder="Configure Saturdays" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Saturdays Off</SelectItem>
                        <SelectItem value="all">All Saturdays Off</SelectItem>
                        <SelectItem value="even">Even Saturdays Off</SelectItem>
                        <SelectItem value="odd">Odd Saturdays Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Sunday Schedule</label>
                    <Select
                      value={weekendConfig.sunday}
                      onValueChange={(value) =>
                        setWeekendConfig((prev) => ({
                          ...prev,
                          sunday: value,
                        }))
                      }
                    >
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder="Configure Sundays" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Sundays Off</SelectItem>
                        <SelectItem value="all">All Sundays Off</SelectItem>
                        <SelectItem value="even">Even Sundays Off</SelectItem>
                        <SelectItem value="odd">Odd Sundays Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleUpdateWeekendConfig}
                    disabled={weekendLoading}
                    className="w-full bg-gray-900 hover:bg-gray-800"
                  >
                    {weekendLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Save Weekend Settings"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!holidayToDelete} onOpenChange={(open) => !open && setHolidayToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Holiday</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{holidayToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteLoading === holidayToDelete?._id}
            >
              {deleteLoading === holidayToDelete?._id ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Past Holiday Error Dialog */}
      <AlertDialog open={showPastHolidayError} onOpenChange={setShowPastHolidayError}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Delete Past Holiday</AlertDialogTitle>
            <AlertDialogDescription>
              You cannot delete holidays that have already passed. Only future holidays can be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowPastHolidayError(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default HolidayScheduler
